import { replaceLocation } from "../../lib/browser-hacks";
import * as t from "io-ts";
import { isLeft } from "fp-ts/lib/Either";
import { lazyLoading } from "lib/lazy-loading";
import { logInfo } from "common/debug-print";
import type { AppLoginTokenContainer } from "common/firebase-function-types";
import { appLoginTokenContainerCodec } from "common/firebase-function-types";

// Typescript really doesn't like that we're not importing firebase here,
// but that's too bad, because if we tried to import firebase at a module level
// we'd balloon our chunk sizes.
// @ts-ignore
export async function importFirebase(): Promise<typeof firebase.default> {
    const p1 = lazyLoading("firebase-app", false, () => import("firebase/app"));
    const p2 = lazyLoading("firebase-auth", false, () => import("firebase/auth"));
    const [fb] = await Promise.all([p1, p2]);
    return fb.default;
}

export async function signInToFirebaseWithCustomToken(token: string): Promise<firebase.default.auth.UserCredential> {
    const fb = await importFirebase();
    const auth = fb.auth();
    const result = await auth.signInWithCustomToken(token);
    await auth.setPersistence(fb.auth.Auth.Persistence.NONE);
    return result;
}

export async function hookFirebaseSignOutTransitionEvent(handler: () => void) {
    const fb = await importFirebase();
    const auth = fb.auth();
    let signedInBefore = false;
    // Note that this handler will fire immediately if we are signed in,
    // but defer until determinate (which could also be "not logged in").
    // Hence the weird logic here.
    //
    // We fire immediately on determinate login state. This could have
    // already happened before we are called. By "determinate" we mean
    // that we definitely know whether or not we are authenticated with
    // Firebase. This could mean that we have a User, or we could not
    // have a User. But we definitely know what we do or don't have.
    //
    // The expected authentication transition for any given app is
    // not signed in -> signed in. We might have missed that transition,
    // but we'll still be able to capture it if we're already signed in.
    // Once we're signed in, we need to fire the handler when we sign out.
    auth.onAuthStateChanged(u => {
        logInfo("Player Firebase auth state changed", u);
        if (u !== null && !signedInBefore) {
            signedInBefore = true;
            return;
        }

        if (u === null && signedInBefore) {
            handler();
        }
    });
}

export function convertOAuth2RedirectTokenToState(): string | undefined {
    const { oauth2RedirectToken } = window as any;
    if (oauth2RedirectToken === undefined) return undefined;

    return btoa(JSON.stringify({ source: window.location.href, redirectToken: oauth2RedirectToken }));
}

export function extractOAuth2AuthTokenFromCurrentURL(): string | undefined {
    // OAuth2 Auth tokens are sent in the hash fragment to avoid calling the `play` function again.
    // If we put these in the search parameters, as far as Firebase Hosting and the browser know
    // we're making a request to something completely different, but if only the hash fragment has
    // changed then both the browser and Firebase Hosting can read the `play` function response from
    // the cache again.
    // FIXME: This usage of the hash fragment is pretty fragile. We should standardize on something.
    // window.location.hash[0] === "#" if there's actually a hash, so we need to get rid of it.
    const { location } = window;
    const [hashKey, hashValue] = location.hash.substring(1).split("=").map(decodeURIComponent);
    const oauth2AuthToken = hashKey === "signInWithOauth2Token" ? hashValue : undefined;
    if (oauth2AuthToken !== undefined) {
        const hashlessURL = `${location.protocol}//${location.host}${location.pathname}`;
        replaceLocation(hashlessURL);
    }
    return oauth2AuthToken;
}

const oauth2AuthTokenMessageKind = "oauth2-auth-token";
const oAuth2AuthTokenMessageCodec = t.type({
    kind: t.literal(oauth2AuthTokenMessageKind),
    authToken: t.string,
});

export type OAuth2AuthTokenMessage = t.TypeOf<typeof oAuth2AuthTokenMessageCodec>;

export function encodeOAuth2AuthTokenMessage(authToken: string): OAuth2AuthTokenMessage {
    return { kind: oauth2AuthTokenMessageKind, authToken };
}

export function decodeOAuth2AuthTokenMessage(o: object): OAuth2AuthTokenMessage | undefined {
    const decoded = oAuth2AuthTokenMessageCodec.decode(o);
    return isLeft(decoded) ? undefined : decoded.right;
}

const oAuth2AuthErrorMessageKind = "oauth2-auth-error";
const oAuth2AuthErrorMessageCodec = t.type({
    kind: t.literal(oAuth2AuthErrorMessageKind),
});

type OAuth2AuthErrorMessage = t.TypeOf<typeof oAuth2AuthErrorMessageCodec>;

// FIXME: Expose error conditions in a more granular format
export function encodeOAuth2AuthErrorMessage(): OAuth2AuthErrorMessage {
    return { kind: oAuth2AuthErrorMessageKind };
}

export function decodeOAuth2AuthErrorMessage(o: object): OAuth2AuthErrorMessage | undefined {
    const decoded = oAuth2AuthErrorMessageCodec.decode(o);
    return isLeft(decoded) ? undefined : decoded.right;
}

export function extractAppLoginTokenFromCurrentURL(): AppLoginTokenContainer | undefined {
    const { location } = window;
    const [hashKey, hashValue] = location.hash.substring(1).split("=").map(decodeURIComponent);
    if (hashKey !== "appLoginToken") return undefined;

    const hashlessURL = `${location.protocol}//${location.host}${location.pathname}`;
    replaceLocation(hashlessURL);

    try {
        const decoded = appLoginTokenContainerCodec.decode(JSON.parse(hashValue));
        if (isLeft(decoded)) return undefined;
        return decoded.right;
    } catch {
        return undefined;
    }
}
